import { useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ScanSearch, ShieldCheck, ShieldAlert, Loader2, X, Check, Minus, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VerifyTool = () => {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | checking | done
  const [report, setReport] = useState(null);
  const inputRef = useRef(null);

  const validate = (f) => {
    const n = f.name.toLowerCase();
    if (!n.endsWith(".pdf") && !n.endsWith(".epub")) {
      toast.error("Unsupported file", { description: "Only .pdf and .epub files are accepted." });
      return false;
    }
    if (f.size > 40 * 1024 * 1024) {
      toast.error("File too large", { description: "Maximum size is 40 MB." });
      return false;
    }
    return true;
  };

  const onFiles = (f) => { if (f && validate(f)) { setFile(f); setReport(null); setPhase("idle"); } };

  const onDrop = useCallback((e) => { e.preventDefault(); setDragging(false); onFiles(e.dataTransfer.files?.[0]); }, []);

  const verify = async () => {
    if (!file) return;
    setPhase("checking");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/verify`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
      setPhase("done");
    } catch (e) {
      setPhase("idle");
      toast.error("Verification failed", { description: e.response?.data?.detail || "Could not read the file." });
    }
  };

  const reset = () => { setFile(null); setReport(null); setPhase("idle"); };
  const kb = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(2)} MB` : `${(b / 1024).toFixed(1)} KB`);

  const downloadProofCard = async () => {
    if (!report) return;
    try {
      await document.fonts.load("900 60px Cinzel");
      await document.fonts.load("700 22px 'JetBrains Mono'");
    } catch { /* fonts optional */ }
    const W = 1200, H = 630;
    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "#041b24"; ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "#70250a"; ctx.lineWidth = 6; ctx.strokeRect(24, 24, W - 48, H - 48);

    // shield glyph
    ctx.save();
    ctx.translate(90, 90); ctx.scale(2.6, 2.6);
    ctx.strokeStyle = "#70250a"; ctx.lineWidth = 1.6; ctx.lineJoin = "round"; ctx.lineCap = "round";
    const p = new Path2D("M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z");
    const c = new Path2D("m9 12 2 2 4-4");
    ctx.stroke(p); ctx.stroke(c);
    ctx.restore();

    ctx.fillStyle = "#7c8a8b";
    ctx.font = "700 24px 'JetBrains Mono', monospace";
    ctx.fillText("VERIFIED · ANTI-AI PROTECTED", 170, 92);

    ctx.fillStyle = "#E6E2D6";
    ctx.font = "900 78px Cinzel, Georgia, serif";
    ctx.fillText("INKSHROUD", 170, 168);

    // filename
    ctx.fillStyle = "rgba(230,226,214,0.75)";
    ctx.font = "500 30px Lora, Georgia, serif";
    const fname = (report.filename || "manuscript").slice(0, 46);
    ctx.fillText(`“${fname}”`, 90, 260);

    // layer stats
    const L = report.layers;
    const stats = [
      ["Invisible prompts", L.invisible_prompts],
      ["Decoy false facts", L.decoy_facts],
      ["Zero-width chars", L.zero_width_characters],
      ["Homoglyphs", L.homoglyphs],
      ["Metadata poisoned", L.metadata_poisoned ? "YES" : "NO"],
    ];
    let y = 360;
    stats.forEach(([label, val]) => {
      ctx.fillStyle = "#485354"; ctx.font = "400 26px 'JetBrains Mono', monospace";
      ctx.fillText(label, 90, y);
      ctx.fillStyle = "#6d411c"; ctx.textAlign = "right";
      ctx.fillText(String(val), W - 90, y);
      ctx.textAlign = "left";
      ctx.strokeStyle = "rgba(72,83,84,0.3)"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(90, y + 16); ctx.lineTo(W - 90, y + 16); ctx.stroke();
      y += 48;
    });

    ctx.fillStyle = "#7c8a8b"; ctx.font = "400 20px 'JetBrains Mono', monospace";
    const date = new Date().toISOString().slice(0, 10);
    ctx.fillText(`Verified ${date}`, 90, H - 60);
    ctx.textAlign = "right";
    ctx.fillStyle = "#70250a";
    ctx.fillText("Shroud your words from the machines.", W - 90, H - 60);
    ctx.textAlign = "left";

    canvas.toBlob((b) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b);
      a.download = "inkshroud-proof.png";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Proof card downloaded");
    }, "image/png");
  };

  const Row = ({ label, value, active }) => (
    <div className="flex items-center justify-between py-2.5 border-t border-[#485354]/20 first:border-t-0">
      <span className="font-lora text-sm text-[#E6E2D6]/70">{label}</span>
      <span className={`font-mono text-xs flex items-center gap-2 ${active ? "text-[#6d411c]" : "text-[#485354]"}`}>
        {value}
        {active ? <Check className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
      </span>
    </div>
  );

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="verify-tool">
      <div className="lg:col-span-3">
        {phase !== "done" && (
          <div
            data-testid="verify-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => phase === "idle" && inputRef.current?.click()}
            className={`h-full min-h-[360px] flex flex-col items-center justify-center text-center px-8 py-14 border border-dashed transition-colors duration-300 ${
              phase === "checking" ? "border-[#485354]/60 bg-[#041b24]/60 cursor-default"
              : dragging ? "border-[#70250a] bg-[#70250a]/5 cursor-pointer" : "border-[#485354]/60 bg-[#041b24]/50 hover:border-[#70250a] cursor-pointer"
            }`}
          >
            <input ref={inputRef} type="file" accept=".pdf,.epub" className="hidden" data-testid="verify-file-input" onChange={(e) => onFiles(e.target.files?.[0])} />
            {phase === "checking" ? (
              <>
                <Loader2 className="w-10 h-10 text-[#6d411c] mb-5 animate-spin" strokeWidth={1.3} />
                <p className="font-cinzel text-xl text-[#E6E2D6]">Scanning for hidden layers…</p>
              </>
            ) : (
              <>
                <ScanSearch className="w-11 h-11 text-[#485354] mb-5" strokeWidth={1.2} />
                {file ? (
                  <>
                    <p className="font-cinzel text-lg text-[#E6E2D6] mb-1" data-testid="verify-file-name">{file.name}</p>
                    <p className="font-mono text-xs text-[#485354] uppercase tracking-widest">{kb(file.size)} · ready to scan</p>
                    <button data-testid="verify-clear-btn" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#485354] hover:text-[#70250a] transition-colors">
                      <X className="w-3.5 h-3.5" /> remove
                    </button>
                  </>
                ) : (
                  <>
                    <p className="font-cinzel text-2xl text-[#E6E2D6] mb-3">Check a file</p>
                    <p className="font-lora text-[#E6E2D6]/70 max-w-sm mb-5">Drop a protected book to confirm the shrouding is intact — or test an original to see the difference.</p>
                    <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#485354] border border-[#485354]/50 px-4 py-2">.pdf · .epub</span>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {phase === "done" && report && (
          <div className={`h-full min-h-[360px] flex flex-col items-center justify-center text-center px-8 py-14 border ${report.shrouded ? "border-[#6d411c]/50 bg-[#041b24]/60" : "border-[#70250a]/50 bg-[#1a0a06]/40"}`} data-testid="verify-result">
            {report.shrouded ? (
              <ShieldCheck className="w-14 h-14 text-[#6d411c] mb-5" strokeWidth={1.3} />
            ) : (
              <ShieldAlert className="w-14 h-14 text-[#70250a] mb-5" strokeWidth={1.3} />
            )}
            <p className="font-cinzel text-2xl text-[#E6E2D6] mb-2" data-testid="verify-verdict">
              {report.shrouded ? "Shrouding confirmed" : "No shrouding detected"}
            </p>
            <p className="font-lora text-[#E6E2D6]/60 max-w-md mb-8">
              {report.shrouded
                ? "This file carries active anti-AI payload. Human readers see nothing; scrapers get poison."
                : "This file appears to be an unprotected original. Run it through the Shrouding Table to shroud it."}
            </p>
            <button data-testid="verify-again-btn" onClick={reset} className="inline-flex items-center gap-2 border border-[#485354] text-[#E6E2D6] font-cinzel font-bold uppercase tracking-widest text-sm py-4 px-8 hover:border-[#6d411c] hover:bg-[#041b24] transition-colors duration-300">
              Check another
            </button>
            {report.shrouded && (
              <button data-testid="download-proof-btn" onClick={downloadProofCard} className="mt-4 inline-flex items-center gap-2 bg-[#70250a] text-[#E6E2D6] font-cinzel font-bold uppercase tracking-widest text-sm py-4 px-8 hover:bg-[#8A2E0D] transition-colors duration-300">
                <Download className="w-4 h-4" /> Download proof card
              </button>
            )}
          </div>
        )}
      </div>

      <div className="lg:col-span-2 flex flex-col gap-6">
        {phase === "done" && report ? (
          <>
            <div className="border border-[#485354]/50 bg-[#041b24]/80 p-7" data-testid="verify-report">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#485354] mb-5">Detected layers</p>
              <Row label="Invisible prompts" value={report.layers.invisible_prompts} active={report.layers.invisible_prompts > 0} />
              <Row label="Decoy false facts" value={report.layers.decoy_facts} active={report.layers.decoy_facts > 0} />
              <Row label="Zero-width chars" value={report.layers.zero_width_characters} active={report.layers.zero_width_characters > 0} />
              <Row label="Homoglyphs" value={report.layers.homoglyphs} active={report.layers.homoglyphs > 0} />
              <Row label="Poisoned metadata" value={report.layers.metadata_poisoned ? "yes" : "no"} active={report.layers.metadata_poisoned} />
            </div>
            {report.samples?.length > 0 && (
              <div className="border border-[#6d411c]/40 bg-[#0a2632]/60 p-6" data-testid="verify-samples">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#485354] mb-4">Hidden content captured</p>
                {report.samples.map((s, i) => (
                  <p key={i} className="font-mono text-[11px] text-[#E6E2D6]/70 leading-relaxed mb-3 last:mb-0 border-l-2 border-[#70250a] pl-3">“{s}”</p>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="border border-[#485354]/50 bg-[#041b24]/80 p-7">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#485354] mb-4">What this scans for</p>
              <ul className="space-y-3 font-lora text-sm text-[#E6E2D6]/60">
                <li>Invisible prompt-injection directives</li>
                <li>Zero-width & homoglyph noise</li>
                <li>Decoy false information</li>
                <li>Poisoned document metadata</li>
              </ul>
            </div>
            <button
              data-testid="verify-button"
              disabled={!file || phase === "checking"}
              onClick={verify}
              className="w-full inline-flex items-center justify-center gap-3 border-2 border-[#6d411c] text-[#E6E2D6] font-cinzel font-bold uppercase tracking-widest text-sm py-6 px-8 hover:bg-[#6d411c]/20 hover:border-[#70250a] transition-colors duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {phase === "checking" ? <><Loader2 className="w-5 h-5 animate-spin" /> Scanning…</> : <><ScanSearch className="w-5 h-5" /> Verify shrouding</>}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
